import { HttpClient } from '@angular/common/http';
import { Component, OnInit, Input, EventEmitter, ViewChild, ElementRef, OnDestroy, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Path, TreeNode, TreeResource } from '@s8l/client-tree-lib';
import { combineLatest } from 'rxjs';
import * as tus from 'tus-js-client';

import { AlertService } from 'src/app/services/alert.service';
import {
  VimeoStatusResponse,
  CmsServerService,
  VimeoListResponseEntry,
  VimeoFolderResponseEntry
} from 'src/app/services/jsonrpc/cms-server.service';
import { CmsService } from 'src/app/services/cms.service';
import { NotificationsService } from 'src/app/services/notifications.service';

import { FileUploadComponent } from '../file-upload/file-upload.component';

@Component({
  selector: 'app-vimeo-explorer',
  templateUrl: './vimeo-explorer.component.html',
  styleUrls: ['./vimeo-explorer.component.scss']
})
export class VimeoExplorerComponent implements OnInit, OnDestroy {
  @ViewChild(FileUploadComponent) private fileUpload: FileUploadComponent;

  @Input() public edit = false;
  @Input() public selected: VimeoListResponseEntry;

  @Output() public selectedChange = new EventEmitter<VimeoListResponseEntry>();
  @Output() public selectedVideosChange = new EventEmitter<VimeoListResponseEntry[]>();

  @ViewChild('thumbnailInput', { static: true }) public thumbnailInput: ElementRef<HTMLInputElement>;
  @ViewChild('createFolderModal', { static: true }) public createFolderModal;
  @ViewChild('moveModal', { static: true }) public moveModal;

  public authLink: string;
  public status: VimeoStatusResponse;
  public videos: any[] = null;
  public videoMap: { [index: string]: any } = null;

  public uploadProgress = {};
  public uploading = false;

  public folder = null;
  public folders: VimeoFolderResponseEntry[] = [];

  public selectedVideos = [];
  public selectedFolder = null;

  public progress = null;

  public fileTypes = {
    'video/mp4': 'video'
  };

  private transcodeTimeout = null;

  public get currentVideos() {
    if (this.selectedFolder === null) {
      return this.videos;
    }
    return this.videos.find(v => {
      return v.type === 'folder' && v.name === this.selectedFolder;
    }).children;
  }

  constructor(
    private cms: CmsService,
    private server: CmsServerService,
    private route: ActivatedRoute,
    private router: Router,
    private alert: AlertService,
    private notification: NotificationsService,
    private http: HttpClient,
    private modal: NgbModal
  ) {}

  public ngOnInit(): void {
    combineLatest([this.route.data, this.route.queryParams]).subscribe(res => {
      const data = res[0];
      const query = res[1];
      if (!data.auth) {
        this.updateStatus();
      } else {
        this.server
          .vimeoAuth({
            code: query.code,
            state: query.state
          })
          .then(() => this.router.navigate(['/vimeo']));
      }
    });
  }

  public ngOnDestroy() {
    clearTimeout(this.transcodeTimeout);
    this.transcodeTimeout = null;
  }

  private updateStatus() {
    return this.server.vimeoStatus().then(async status => {
      this.status = status;
      if (!status.logged_in) {
        return this.server
          .vimeoAuthUrl()
          .then(url => (this.authLink = url))
          .then(() => null);
      }
      return this.updateVideoList();
    });
  }

  private buildTree(folders, videos) {
    const root = {
      type: 'folder',
      name: 'root',
      children: []
    };

    for (const f of folders) {
      root.children.push({
        type: 'folder',
        children: [],
        ...f
      });
    }

    for (const v of videos) {
      const p = Path.parse(v.path);
      const segments = p.get();

      let current = root;
      for (let i = 0; i < segments.length; i++) {
        const child = current.children.find(c => {
          return c.name === segments[i];
        });
        if (child) {
          current = child;
          continue;
        }

        let file = {
          type: 'folder',
          name: segments[i],
          children: []
        };
        if (i + 1 === segments.length) {
          file = {
            ...v,
            type: 'file'
          };
        }
        current.children.push(file);
        current = file;
      }
    }

    return root.children.sort((a, b) => {
      if (a.type === 'folder' && b.type === 'folder') {
        return a.name.localeCompare(b.name);
      }
      if (a.type === 'folder') {
        return -1;
      }
      if (b.type === 'folder') {
        return 1;
      }
      return a.name.localeCompare(b.name);
    });
  }

  private updateVideoList() {
    return this.server.vimeoList().then(videos => {
      return this.server.vimeoListFolder().then(folders => {
        this.folders = folders;
        this.videos = this.buildTree(folders, videos);

        this.videoMap = {};
        for (const v of videos) {
          this.videoMap[v.id] = v;
        }
      });
    });
  }

  public cleanFileUpload() {
    this.uploadProgress = {};
    this.uploading = false;
    this.fileUpload.reset();
  }

  public async upload(files: { title: string; file: File }[]) {
    this.uploading = true;

    const folder = this.folders.find(f => f.name === this.selectedFolder);

    const batchSize = 10;
    const batches = Math.floor(files.length / batchSize) + (files.length % batchSize != 0 ? 1 : 0);
    for (let i = 0; i < batches; i++) {
      const batch = files.slice(i * batchSize, i * batchSize + batchSize);
      const promises = batch.map(f => {
        const index = f.title.lastIndexOf('.');
        const name = index !== -1 ? f.title.substr(0, index) : f.title;
        return this.server
          .vimeoUpload({
            filename: name,
            size: f.file.size.toString()
          })
          .then(data => this.uploadFile(data.url, f.file))
          .then(() => name);
      });
      await Promise.all(promises).then(names => {
        if (!folder) {
          return;
        }

        return this.server
          .vimeoList()
          .then(videos => {
            return videos
              .filter(v => names.includes(v.name))
              .map(v => {
                return this.server.vimeoMove({
                  folder: folder.id,
                  video: v.id
                });
              });
          })
          .then(moveVideos => Promise.all(moveVideos));
      });
    }

    return this.updateVideoList()
      .then(() => this.cleanFileUpload())
      .catch(() => this.cleanFileUpload());
  }

  public async updateWhitelist(video) {
    if (video.type == 'folder') {
      return;
    }

    return this.server
      .vimeoWhitelist({
        video: video.id
      })
      .then(() => this.updateVideoList());
  }

  public logout() {
    return this.server.vimeoLogout().then(() => this.updateStatus());
  }

  public async delete(f: any) {
    const typeName = f.type === 'file' ? 'video' : 'folder';
    const result = await this.alert.fireWarning(`Deleting ${typeName}`, `You are about to delete this ${typeName}, are you sure?`);
    if (!result.value) {
      return;
    }

    if (f.type === 'folder') {
      return this.server.vimeoDeleteFolder({ folder: f.id }).then(() => this.updateVideoList());
    }
    return this.server.vimeoDelete({ video: f.id }).then(() => this.updateVideoList());
  }

  public async massDelete() {
    const result = await this.alert.fireWarning(
      `Deleting multiple videos`,
      `You are about to delete ${this.selectedVideos.length} videos, are you sure?`
    );
    if (!result.value) {
      return;
    }

    return Promise.all(
      this.selectedVideos.map(id => {
        return this.server.vimeoDelete({ video: id });
      })
    )
      .then(() => (this.selectedVideos = []))
      .then(() => this.updateVideoList());
  }

  public async massUpdateWhitelist() {
    let videos = this.selectedVideos;
    if (videos.length == 0) {
      videos = this.currentVideos.flatMap(v => {
        if (v.type == 'folder') {
          return v.children;
        }
        return [v];
      });
    }

    videos = videos.map(v => () => this.server.vimeoWhitelist({ video: v.id }));

    await this.runBatched(videos);

    this.selectedVideos = [];
    await this.updateVideoList();
  }

  private findVideoArticles(node: TreeNode): Path[] {
    let articles = [];
    if (node.hasResource(TreeResource.Video)) {
      articles.push(Path.parse(node.path));
    }
    for (const c of node.children) {
      articles = [...articles, ...this.findVideoArticles(c)];
    }
    return articles;
  }

  public async updateThumbnail(path: Path) {
    const videos = await this.server.wikiGet({ path: path.uri() }, 'video').then(article => {
      if (article.fields?.content?.type == 'json') {
        return article.fields?.content?.value;
      }
      return [];
    });

    for (let i = 0; i < videos.length; i++) {
      const v = videos[i];
      const update = this.videoMap[v.id];
      if (!update) {
        continue;
      }
      videos[i] = {
        ...v,
        ...update
      };
    }

    return this.server.wikiCreate(
      {
        path: path.uri(),
        fields: [{ name: 'content', type: 'json', content: JSON.stringify(videos) }]
      },
      'video'
    );
  }

  public async massUpdateThumbnails() {
    const node = this.cms.tree.value.get(new Path());
    const articles = this.findVideoArticles(node);

    const promises = articles.map(a => this.updateThumbnail.bind(this, a));
    await this.runBatched(promises, 5);
  }

  public select(v: any) {
    if (v.type === 'folder') {
      this.selectedFolder = v.name;
      return;
    }

    if (this.edit) {
      if (this.selectedVideos.includes(v.id)) {
        this.selectedVideos = this.selectedVideos.filter(id => id != v.id);
      } else {
        this.selectedVideos.push(v.id);
      }
      this.selectedVideosChange.emit(this.selectedVideos);
      return;
    }

    this.selectedChange.emit(v);
    this.selected = v;
  }

  public update(v: VimeoListResponseEntry) {
    return this.server
      .vimeoUpdate({
        video: v.id,
        name: v.name
      })
      .then(() => this.notification.notify('success', 'Updated!', 'The videos were successfully updated.'));
  }

  public async thumbnail(v: VimeoListResponseEntry) {
    const filePromise = new Promise((resolve, reject) => {
      this.thumbnailInput.nativeElement.addEventListener('change', evt => {
        const target = evt.target as HTMLInputElement;
        if (target.files.length) {
          resolve(target.files.item(0));
        } else {
          reject();
        }
      });
      this.thumbnailInput.nativeElement.click();
    });

    const file = await filePromise.catch(() => null);
    if (file === null) {
      return;
    }

    const thumb = await this.server.vimeoThumbnailUrl({ video: v.id });
    await this.http.put(thumb.upload_url, file).toPromise();

    return this.server
      .vimeoThumbnailSet({
        picture_url: thumb.picture_url
      })
      .then(() => this.updateVideoList())
      .then(() => this.notification.notify('success', 'Updated!', 'The videos were successfully updated.'));
  }

  public addFolder() {
    const modal = this.modal.open(this.createFolderModal, {});
    return modal.result
      .then(name => {
        return this.server.vimeoCreateFolder({
          folder: name
        });
      })
      .then(() => this.updateVideoList())
      .catch(() => {})
      .finally(() => {
        this.folder = null;
      });
  }

  public move(v) {
    const modal = this.modal.open(this.moveModal, {});
    return modal.result
      .then(index => {
        const f = this.folders[index];
        return this.server.vimeoMove({
          folder: f.id,
          video: v.id
        });
      })
      .then(() => this.updateVideoList())
      .catch(() => {})
      .finally(() => {
        this.folder = null;
      });
  }

  private uploadFile(url, file) {
    return new Promise<void>((resolve, reject) => {
      const upload = new tus.Upload(file, {
        endpoint: undefined,
        uploadUrl: url,
        retryDelays: [0, 1000, 3000, 5000],
        onError: err => reject(err),
        onProgress: (bytesUploaded, bytesTotal) => {
          const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
          this.uploadProgress[file.name] = percentage;
        },
        onSuccess: () => resolve()
      });

      upload.start();
    });
  }

  private async runBatched(promises, batchSize = 10) {
    this.progress = 0;

    let done = 0;
    const count = promises.length;
    while (promises.length) {
      await Promise.all(promises.splice(0, batchSize).map(f => f()));

      done += batchSize;
      this.progress = done / count;
    }
    this.progress = null;
  }
}
